/**
 *
 * Dates parsing and handling utils
 *
 * All dates are stored as ISO8601 date & datetime formats
 * recognized by the main date libraries
 */
import dayjs from './dayjs';

// The following syntaxes are recognized by both MomentJS & DateFns
const ISO_DATE = 'YYYY-MM-DD';
const ISO_DATETIME = 'YYYY-MM-DDTHH:mm:ssZ';
const DATETIME = 'YYYY-MM-DD HH:mm:ss';

const getToday = () => dayjs().format(ISO_DATE);
/**
 * Possible UNITS for date manipulation (add, sub)
 * Add more if necessary but ensure it is supported in methods below
 * @type {Object}
 */
const UNITS = {
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
};

/**
 * Possible UNITS for dates comparison (compare, isSame, ...)
 * Add more if necessary but ensure it is supported in methods below
 * @type {Object}
 */
const COMPARISON_UNITS = {
    ...UNITS,
    week: 'week', // local week
    isoWeek: 'isoWeek', // ISO week
};

/**
 * [Internal] - validates "unit" parameter value. Throw if not supported
 * @param  {String} unit
 * @return {undefined}
 * @throws Will throw is date is invalid
 */
const validateUnit = (unit, allowedUnits) => {
    if (!Object.keys(allowedUnits).includes(unit))
        throw new Error(
            `Invalid value for parameter "unit": "${unit}". See UNITS constant for accepted values or add support for a new unit`,
        );
};

/**
 * Add time to a date
 * @param  {String} date
 * @param  {Number} amount
 * @param  {String} unit - one of UNITS
 * @param  {Object} options - extra parameters
 * @param  {String} options.outputFormat - [default=ISO_DATE]. Format of resulting date string
 * @return {String} the new date with the time added
 */
const add = (date, amount, unit, options = { outputFormat: ISO_DATE }) => {
    validateUnit(unit, UNITS);
    return dayjs(date).add(amount, unit).format(options.outputFormat);
};

/**
 * Subtract time from a date
 * @param  {String} date
 * @param  {Number} amount
 * @param  {String} unit - one of UNITS
 * @param  {Object} options - extra parameters
 * @param  {String} options.outputFormat - [default=ISO_DATE]. Format of resulting date string
 * @return {String} the new date with the time subtracted
 */
const subtract = (date, amount, unit, options = { outputFormat: ISO_DATE }) => {
    validateUnit(unit, UNITS);
    return dayjs(date).subtract(amount, unit).format(options.outputFormat);
};

/**
 * Compare 2 dates and return:
 *  0  if the dates are equal
 *  1  if the date1 is after date2
 *  -1 if the date1 is before date2
 * @param {String} date1
 * @param {String} date2
 * @param {String} precision - one of COMPARISON_UNITS
 * @return {Number}  0 if d1 = d2
 *                  -1 if d1 < d2
 *                  +1 if d1 > d2
 */
const compare = (date1, date2, precision = COMPARISON_UNITS.day) => {
    validateUnit(precision, COMPARISON_UNITS);
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);
    if (d1.isSame(d2, precision)) return 0;
    if (d1.isBefore(d2, precision)) return -1;
    return 1;
};

/**
 * Methods to compare dates up to a certain precision.
 * Examples:
 *  isSame('2018-07-01', '2018-07-15', 'month') -> true
 *  isSame('2018-07-01', '2017-07-15', 'month') -> false
 *  isSame('2018-07-02', '2018-11-01', 'month') -> false
 *
 * See COMPARISON_UNITS for "precision" parameter possible values
 */

const isSame = (date1, date2, precision = COMPARISON_UNITS.day) => compare(date1, date2, precision) === 0;
const isAfter = (date1, date2, precision = COMPARISON_UNITS.day) => compare(date1, date2, precision) === 1;
const isBefore = (date1, date2, precision = COMPARISON_UNITS.day) => compare(date1, date2, precision) === -1;
const isSameOrAfter = (date1, date2, precision = COMPARISON_UNITS.day) => compare(date1, date2, precision) >= 0;
const isSameOrBefore = (date1, date2, precision = COMPARISON_UNITS.day) => compare(date1, date2, precision) <= 0;

/**
 * Indicates if date is in the past (day precision)
 * @param  {String}  date
 * @return {Boolean} true if <= yesterday, false otherwise
 */
const isPast = (date) => isBefore(date, dayjs().format(ISO_DATE), COMPARISON_UNITS.day);

/**
 * Indicates if date is in the future (day precision)
 * @param  {String}  date
 * @return {Boolean} true if >= tomorrow, false otherwise
 */
const isFuture = (date) => isAfter(date, dayjs().format(ISO_DATE), COMPARISON_UNITS.day);

/**
 * Formats a date into the given format
 * @param  {String} date
 * @param  {String} format - a string of tokens. See dayjsJS supported
 *                           tokens. Characters between [] will be escaped
 *                           https://dayjsjs.com/docs/#/displaying/format/
 * @return {String} the formatted date
 */
const format = (date, formatString = ISO_DATE) => dayjs(date).format(formatString);

/**
 * Returns the start of a period
 * @example
 * // returns '2018-01-01'
 * startOf('2018-05-25', 'year');
 *
 * @param  {String} date
 * @param  {String} period - one of COMPARISON_UNITS
 * @param  {Object} options - extra parameters
 * @param  {String} options.outputFormat - [default=ISO_DATE]. Formatof resulting date string
 * @return {String} new date representing the start of the period
 */
const startOf = (date, period, options = { outputFormat: ISO_DATE }) => {
    validateUnit(period, COMPARISON_UNITS);
    return dayjs(date).startOf(period).format(options.outputFormat);
};

/**
 * Returns the end of a period
 * @example
 * // returns '2018-01-31'
 * endOf('2018-01-05', 'month');
 *
 * @param  {String} date
 * @param  {String} period - one of COMPARISON_UNITS
 * @param  {Object} options - extra parameters
 * @param  {String} options.outputFormat - [default=ISO_DATE]. Formatof resulting date string
 * @return {String} new date representing the end of the period
 */
const endOf = (date, period, options = { outputFormat: ISO_DATE }) => {
    validateUnit(period, COMPARISON_UNITS);
    return dayjs(date).endOf(period).format(options.outputFormat);
};

/**
 * Returns the ISO week number for the supplied date
 * @param  {String} date
 * @return {Number} ISO week's number (between 1 and 53)
 */
const getIsoWeek = (date) => dayjs(date).isoWeek();

/**
 * Return the date at the SPIT (Same Point In Time) last year
 * @param {String} date
 * @return {String} the date at the SPIT last year
 */
const lySpitOf = (date) => subtract(date, 52, 'week');

/**
 * Return the date at the SPIT (Same Point In Time) 2 years ago
 * @param {String} date
 * @return {String} the date at the SPIT last year
 */
const y2SpitOf = (date) => subtract(date, 104, 'week');

export {
    ISO_DATE,
    ISO_DATETIME,
    DATETIME,
    UNITS,
    COMPARISON_UNITS,
    getToday,
    add,
    subtract,
    compare,
    isSame,
    isAfter,
    isBefore,
    isSameOrAfter,
    isSameOrBefore,
    isPast,
    isFuture,
    format,
    startOf,
    endOf,
    getIsoWeek,
    lySpitOf,
    y2SpitOf,
};
